"use client";

import Link from "next/link";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { FormEvent, useState } from "react";

export default function CreateSchoolPage() {
  const router = useRouter();
  const [schoolName, setSchoolName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await fetch("/api/schools/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schoolName, adminName, email, password }),
      });
      const data = (await res.json()) as { error?: string; joinCode?: string };
      if (!res.ok) {
        setError(data.error ?? "Couldn’t create school.");
        return;
      }

      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });
      if (result?.error) {
        setError("School created — sign in from the login page.");
        router.push("/login");
        return;
      }

      router.push("/admin");
      router.refresh();
    } catch {
      setError("Couldn’t create school — try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-full bg-ink-50 font-sans">
      <main className="mx-auto flex w-full max-w-md flex-col gap-6 px-6 py-16">
        <div>
          <p className="text-sm font-medium tracking-wide text-ink-500 uppercase">
            Daily15
          </p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-ink-950">
            Create your school
          </h1>
          <p className="mt-2 text-sm text-ink-600">
            You’ll become the school admin, get a join code, and approve
            teachers before they can upload questions.
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm font-medium text-ink-700">
            School name
            <input
              type="text"
              required
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              placeholder="Acme Secondary School"
              className="rounded-xl border border-ink-300 bg-white px-4 py-3 text-base font-normal text-ink-950 outline-none ring-brand-500 focus:ring-2"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink-700">
            Your name
            <input
              type="text"
              autoComplete="name"
              required
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              className="rounded-xl border border-ink-300 bg-white px-4 py-3 text-base font-normal text-ink-950 outline-none ring-brand-500 focus:ring-2"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink-700">
            Email
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border border-ink-300 bg-white px-4 py-3 text-base font-normal text-ink-950 outline-none ring-brand-500 focus:ring-2"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink-700">
            Password
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl border border-ink-300 bg-white px-4 py-3 text-base font-normal text-ink-950 outline-none ring-brand-500 focus:ring-2"
            />
            <span className="text-xs font-normal text-ink-500">
              At least 8 characters
            </span>
          </label>

          {error && <p className="text-sm text-rose-600">{error}</p>}
          <button
            type="submit"
            disabled={pending}
            className="rounded-2xl bg-brand-600 px-5 py-3 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {pending ? "Creating…" : "Create school"}
          </button>
        </form>

        <p className="text-sm text-ink-600">
          Teacher joining a school?{" "}
          <Link
            href="/join"
            className="font-medium text-ink-950 underline-offset-2 hover:underline"
          >
            Join with a code
          </Link>
        </p>
        <p className="text-sm text-ink-600">
          Student?{" "}
          <Link
            href="/register"
            className="font-medium text-ink-950 underline-offset-2 hover:underline"
          >
            Create a student account
          </Link>
        </p>
      </main>
    </div>
  );
}
